/**
 * sl-9qm.4.2: SingleArticle Component
 *
 * Full view of a single row's content (rich text).
 * For Row Detail Pages (sl-9qm.12).
 */
import { createElement } from 'react';
import { z } from 'zod';

import { VIEW_COMPONENT_TYPES } from '../../../constants/view-component-types';
import type {
  BoundDataRow,
  ViewComponent,
  ViewRuntimeContext,
} from '../../../types/view-component.types';
import * as styles from '../styles.css';
import { getContentCopy } from './content-copy';
import { hasKbArticleDocument, KbArticleRenderer } from './kb-article-renderer';
import { renderSanitizedMarkdown } from './markdown-sanitizer';

const configSchema = z.object({
  showTitle: z.boolean().default(true),
  showMeta: z.boolean().default(true),
  showBackLink: z.boolean().default(true),
  rowSlug: z.string().default(''),
  preferRuntimeLocale: z.boolean().default(false),
  renderMarkdown: z.boolean().default(false),
});

function readString(
  row: BoundDataRow,
  keys: readonly string[]
): string | undefined {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'string' && value.trim() !== '') return value;
  }
  return undefined;
}

export const SingleArticleComponent: ViewComponent = {
  id: VIEW_COMPONENT_TYPES.SINGLE_ARTICLE,
  type: 'content',
  name: 'Single Article',
  icon: createElement(
    'span',
    { style: { fontSize: 'var(--sl-custom-app-font-size-sm)' } },
    '📄'
  ),
  capabilities: [
    {
      role: 'primary',
      description: 'Single row with article content',
      required: true,
      requirements: [
        { field: 'title', acceptedTypes: ['text'], required: true },
        { field: 'content', acceptedTypes: ['text'], required: true },
        {
          field: 'cover',
          acceptedTypes: ['attachment', 'url'],
          required: false,
        },
        { field: 'alt', acceptedTypes: ['text'], required: false },
        { field: 'meta', acceptedTypes: ['text', 'date'], required: false },
      ],
    },
  ],
  configSchema,
  defaultConfig: {
    showTitle: true,
    showMeta: true,
    showBackLink: true,
    rowSlug: '',
    preferRuntimeLocale: false,
    renderMarkdown: false,
  },
  render({ config, data, runtimeContext }) {
    const {
      showTitle,
      showMeta,
      showBackLink,
      rowSlug,
      preferRuntimeLocale,
      renderMarkdown,
    } = config as z.infer<typeof configSchema>;
    const slugRows = rowSlug
      ? data.filter(row => readString(row, ['slug']) === rowSlug)
      : data;
    const runtimeLocale = runtimeContext?.locale
      ?.trim()
      .toLowerCase()
      .split('-')[0];
    const row =
      (preferRuntimeLocale && runtimeLocale
        ? slugRows.find(
            candidate =>
              readString(candidate, ['locale'])?.trim().toLowerCase() ===
              runtimeLocale
          )
        : undefined) ?? slugRows[0];

    if (!row) {
      return createElement(
        'div',
        {
          className: styles.singleArticle,
          style: {
            color: 'var(--streamlain-text-secondary-color)',
            textAlign: 'center' as const,
          },
        },
        getContentCopy(runtimeContext?.locale, 'articleNoData')
      );
    }

    const title = readString(row, ['title', 'name']) ?? 'Untitled';
    const content = readString(row, ['content', 'body', 'summary']) ?? '';
    const meta = readString(row, ['meta', 'publishedAt', 'date']);

    if (hasKbArticleDocument(row, runtimeContext)) {
      return createElement(KbArticleRenderer, {
        row,
        runtimeContext,
        workspaceId: readWorkspaceId(runtimeContext),
      });
    }

    return createElement(
      'div',
      { className: styles.singleArticle },
      showBackLink &&
        createElement(
          'a',
          {
            href: '#',
            style: {
              fontSize: 'var(--sl-custom-app-font-size-sm)',
              color: 'var(--streamlain-text-secondary-color)',
              textDecoration: 'none',
              marginBottom: 'var(--sl-custom-app-gap-md)',
              display: 'block',
            },
          },
          `← ${getContentCopy(runtimeContext?.locale, 'articleBack')}`
        ),
      showTitle &&
        createElement(
          'h1',
          { className: styles.singleArticleTitle, 'data-sl-field': 'title' },
          title
        ),
      showMeta &&
        meta &&
        createElement(
          'div',
          { className: styles.singleArticleMeta, 'data-sl-field': 'meta' },
          meta
        ),
      renderMarkdown
        ? createElement('div', {
            className: styles.singleArticleContent,
            'data-sl-field': 'content',
            dangerouslySetInnerHTML: {
              __html: renderSanitizedMarkdown(content),
            },
          })
        : createElement(
            'div',
            {
              className: styles.singleArticleContent,
              'data-sl-field': 'content',
            },
            content
          )
    );
  },
  palettePreview() {
    return createElement(
      'div',
      { className: styles.palettePreview },
      '📄 Article'
    );
  },
};

function readWorkspaceId(
  runtimeContext?: ViewRuntimeContext
): string | undefined {
  const value = runtimeContext?.customApp?.['workspaceId'];
  return typeof value === 'string' && value.trim() !== ''
    ? value.trim()
    : undefined;
}
